import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, DollarSign, Gift, ShieldCheck, AlertTriangle, ShieldAlert, Lock, Unlock, CreditCard, Ban } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const CT = 'America/Chicago';
// API timestamps are UTC but come WITHOUT a zone marker (e.g. "2026-09-01T00:07:18").
// JS would parse those as LOCAL time, causing a 5h offset — so mark them UTC first.
const asUTC = (s) => {
    if (!s) return null;
    const str = String(s);
    const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(str);
    return new Date(hasTz ? str : str + 'Z');
};

const VERDICT_META = {
    confirmed: { label: 'Real ✓', color: '#39FF14', bg: 'rgba(57,255,20,0.12)' },
    not_found: { label: 'NOT IN PAYPAL', color: '#ff5470', bg: 'rgba(255,84,112,0.14)' },
    refunded: { label: 'REFUNDED', color: '#ffb020', bg: 'rgba(255,176,32,0.14)' },
    reversed: { label: 'REVERSED', color: '#ff5470', bg: 'rgba(255,84,112,0.14)' },
    pending: { label: 'Pending/Unclear', color: '#ffb020', bg: 'rgba(255,176,32,0.12)' },
    error: { label: 'Check failed', color: '#A0AAB2', bg: 'rgba(255,255,255,0.06)' },
};

// Group loads into day buckets keyed by local date, newest day first.
function groupByDay(items) {
    const buckets = new Map();
    for (const it of items) {
        const d = it.date ? asUTC(it.date) : null;
        const key = d ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: CT }) : 'Unknown date';
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(it);
    }
    return Array.from(buckets.entries());
}

export const FraudWatchPanel = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/payments/admin/flagged-players`, { withCredentials: true });
            setRows(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load flagged players');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleFreeze = async (r) => {
        const freeze = r.status !== 'suspended';
        setBusy(r.user_id);
        try {
            await axios.post(`${API}/payments/admin/freeze-player`,
                { user_id: r.user_id, freeze }, { withCredentials: true });
            toast.success(freeze ? `Froze ${r.name}` : `Unfroze ${r.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Action failed');
        } finally {
            setBusy(null);
        }
    };

    const clearFlag = async (r) => {
        if (!window.confirm(`Approve & unlock ${r.name}? This clears the payment flag and resolves their unconfirmed loads so they can cash out. Only do this after confirming the deposit actually arrived.`)) return;
        setBusy(r.user_id);
        try {
            const { data } = await axios.post(`${API}/payments/admin/clear-payment-flag`,
                { user_id: r.user_id, reason: 'Admin confirmed deposit received' }, { withCredentials: true });
            toast.success(`Unlocked ${r.name} — ${data.loads_resolved} load(s) resolved`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Action failed');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="card-dark p-6 mb-5" data-testid="fraud-watch-panel"
             style={{ border: '1px solid rgba(255,84,112,0.35)' }}>
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                         style={{ background: 'rgba(255,84,112,0.14)', border: '1px solid rgba(255,84,112,0.4)' }}>
                        <ShieldAlert className="w-5 h-5" style={{ color: '#ff5470' }} />
                    </div>
                    <div>
                        <h3 className="text-white font-black text-[15px] leading-tight">Fraud Watch</h3>
                        <p className="text-[11px] text-[#A0AAB2]">Players with reversed / unconfirmed loads</p>
                    </div>
                </div>
                <button onClick={load} disabled={loading}
                        className="p-2 rounded-lg text-[#A0AAB2] hover:text-white transition-colors"
                        data-testid="fraud-refresh" aria-label="Refresh">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {loading ? (
                <p className="text-[13px] text-[#A0AAB2]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-[13px] text-[#39FF14] font-bold" data-testid="fraud-empty">✓ No flagged players — all clear.</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => {
                        const frozen = r.status === 'suspended';
                        return (
                            <div key={r.user_id} data-testid="fraud-row"
                                 className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                                 style={{ background: 'rgba(255,84,112,0.08)', border: '1px solid rgba(255,84,112,0.25)' }}>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-white font-bold text-[13px] truncate">{r.name}</span>
                                        {frozen && <span className="text-[9px] font-black px-1.5 py-0.5 rounded"
                                            style={{ color: '#ffb020', background: 'rgba(255,176,32,0.15)' }}>FROZEN</span>}
                                    </div>
                                    <div className="text-[11px] text-[#A0AAB2] truncate">{r.email || r.phone}</div>
                                    <div className="text-[10px] text-[#ff5470] truncate">{r.flag_reason}</div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-[10px] text-[#A0AAB2]">bad loads</div>
                                    <div className="font-black font-mono text-[14px]" style={{ color: '#ff5470' }}>{money(r.problem_total)}</div>
                                    <div className="text-[10px] text-[#A0AAB2]">bal {money(r.balance)}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => clearFlag(r)} disabled={busy === r.user_id}
                                            data-testid="fraud-clear-btn"
                                            title="Approve & unlock — clears the flag and resolves unconfirmed loads so they can cash out"
                                            className="px-3 py-2 rounded-lg font-bold text-[12px] flex items-center gap-1.5 transition-colors"
                                            style={{ background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.35)', color: '#39FF14' }}>
                                        <Unlock className="w-4 h-4" /> Approve
                                    </button>
                                    <button onClick={() => toggleFreeze(r)} disabled={busy === r.user_id}
                                            data-testid="fraud-freeze-btn"
                                            className="px-3 py-2 rounded-lg font-bold text-[12px] flex items-center gap-1.5 transition-colors"
                                            style={frozen
                                                ? { background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.35)', color: '#39FF14' }
                                                : { background: 'rgba(255,84,112,0.15)', border: '1px solid rgba(255,84,112,0.5)', color: '#ff5470' }}>
                                        {frozen ? <><Unlock className="w-4 h-4" /> Unfreeze</> : <><Lock className="w-4 h-4" /> Freeze</>}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const PayPalLoadsAdminTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [busyUser, setBusyUser] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/payments/admin/loads`, { withCredentials: true });
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load PayPal loads');
        } finally {
            setLoading(false);
        }
    }, []);

    const toggleLoads = useCallback(async (r) => {
        const block = !r.loads_blocked;
        if (block && !window.confirm(`Turn OFF all future PayPal/online loads for ${r.name}?\nThey can still log in and play — they just can't top up until you turn it back on.`)) return;
        setBusyUser(r.user_id);
        // optimistic: flip every row for this player
        setItems((prev) => prev.map((it) => it.user_id === r.user_id ? { ...it, loads_blocked: block } : it));
        try {
            await axios.post(`${API}/payments/admin/block-loads`,
                { user_id: r.user_id, block }, { withCredentials: true });
            toast.success(block ? `${r.name}'s loads turned OFF` : `${r.name}'s loads turned back ON`);
        } catch (e) {
            setItems((prev) => prev.map((it) => it.user_id === r.user_id ? { ...it, loads_blocked: !block } : it));
            toast.error(e.response?.data?.detail || 'Action failed');
        } finally {
            setBusyUser(null);
        }
    }, []);

    const verify = useCallback(async () => {
        setVerifying(true);
        setVerifyResult(null);
        try {
            const { data } = await axios.get(`${API}/payments/admin/verify-loads?limit=300`, { withCredentials: true });
            setVerifyResult(data);
            const bad = (data.counts?.not_found || 0) + (data.counts?.refunded || 0) + (data.counts?.reversed || 0);
            if (bad > 0) toast.error(`${bad} load(s) NOT confirmed by PayPal — ${money(data.problem_total)} flagged`);
            else toast.success('All checked loads confirmed real by PayPal');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Verification failed');
        } finally {
            setVerifying(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const grouped = groupByDay(items);
    const grandTotal = items.reduce((s, it) => s + Number(it.amount_usd || 0), 0);

    return (
        <div data-testid="paypal-loads-tab-wrap">
        <FraudWatchPanel />
        <div className="card-dark p-6" data-testid="paypal-loads-tab">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                         style={{ background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.35)' }}>
                        <DollarSign className="w-4 h-4 text-[#39FF14]" strokeWidth={2.6} />
                    </div>
                    <div>
                        <div className="text-white font-black text-[15px] leading-tight">PayPal Loads</div>
                        <div className="text-[11px] text-[#A0AAB2]">Everyone who topped up with real money via PayPal</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-[#A0AAB2]">Total loaded</div>
                        <div className="text-[#39FF14] font-black font-mono text-lg" data-testid="paypal-loads-total">{money(grandTotal)}</div>
                    </div>
                    <button onClick={verify} disabled={verifying || loading}
                            className="px-3 py-2 rounded-lg font-bold text-[12px] flex items-center gap-1.5 transition-colors"
                            style={{ background: 'rgba(57,255,20,0.12)', border: '1px solid rgba(57,255,20,0.35)', color: '#39FF14' }}
                            data-testid="paypal-verify-btn">
                        <ShieldCheck className={`w-4 h-4 ${verifying ? 'animate-pulse' : ''}`} />
                        {verifying ? 'Checking PayPal…' : 'Verify against PayPal'}
                    </button>
                    <button onClick={load} disabled={loading}
                            className="p-2 rounded-lg text-[#A0AAB2] hover:text-white transition-colors"
                            data-testid="paypal-loads-refresh" aria-label="Refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {verifyResult && (
                <div className="mb-5" data-testid="paypal-verify-result">
                    {verifyResult.env !== 'live' && (
                        <div className="mb-2 px-3 py-2 rounded-lg text-[12px] font-bold flex items-center gap-2"
                             style={{ background: 'rgba(255,176,32,0.12)', border: '1px solid rgba(255,176,32,0.4)', color: '#ffb020' }}>
                            <AlertTriangle className="w-4 h-4" /> This backend is in PayPal <b>{String(verifyResult.env).toUpperCase()}</b> mode — run this on your LIVE production site for real results.
                        </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                        <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(57,255,20,0.10)', border: '1px solid rgba(57,255,20,0.3)' }}>
                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Confirmed real</div>
                            <div className="text-[#39FF14] font-black font-mono text-lg" data-testid="verify-confirmed-total">{money(verifyResult.confirmed_total)}</div>
                            <div className="text-[11px] text-[#A0AAB2]">{verifyResult.counts?.confirmed || 0} loads</div>
                        </div>
                        <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,84,112,0.12)', border: '1px solid rgba(255,84,112,0.4)' }}>
                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Not in PayPal / reversed</div>
                            <div className="text-[#ff5470] font-black font-mono text-lg" data-testid="verify-problem-total">{money(verifyResult.problem_total)}</div>
                            <div className="text-[11px] text-[#A0AAB2]">
                                {(verifyResult.counts?.not_found || 0)} missing · {(verifyResult.counts?.refunded || 0)} refunded · {(verifyResult.counts?.reversed || 0)} reversed
                            </div>
                        </div>
                        <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,176,32,0.10)', border: '1px solid rgba(255,176,32,0.3)' }}>
                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Pending / unclear</div>
                            <div className="text-[#ffb020] font-black font-mono text-lg">{verifyResult.counts?.pending || 0}</div>
                        </div>
                        <div className="rounded-lg px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Checked</div>
                            <div className="text-white font-black font-mono text-lg">{verifyResult.checked}</div>
                        </div>
                    </div>
                    {(() => {
                        const flagged = (verifyResult.items || []).filter(it => ['not_found', 'refunded', 'reversed'].includes(it.verdict));
                        if (flagged.length === 0) return (
                            <p className="text-[13px] text-[#39FF14] font-bold">✓ No phantom loads found in the checked window.</p>
                        );
                        return (
                            <div>
                                <div className="text-[12px] font-black text-[#ff5470] mb-1.5 uppercase tracking-wide">⚠ Flagged loads (money you did NOT receive)</div>
                                <div className="space-y-1">
                                    {flagged.map((it, i) => {
                                        const m = VERDICT_META[it.verdict] || VERDICT_META.error;
                                        return (
                                            <div key={i} data-testid="verify-flagged-row"
                                                 className="flex items-center gap-3 px-3 py-2 rounded-lg"
                                                 style={{ background: m.bg, border: `1px solid ${m.color}55` }}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-white font-bold text-[13px] truncate">{it.name}</div>
                                                    <div className="text-[10px] text-[#A0AAB2] font-mono truncate">{it.capture_id}</div>
                                                </div>
                                                <span className="text-[10px] font-black px-2 py-0.5 rounded" style={{ color: m.color, background: 'rgba(0,0,0,0.25)' }}>{m.label}</span>
                                                <div className="text-right shrink-0 w-16">
                                                    <div className="font-black font-mono text-[14px]" style={{ color: m.color }}>{money(it.amount_usd)}</div>
                                                    <div className="text-[10px] text-[#666]">{it.date ? asUTC(it.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: CT }) : ''}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-12"><div className="spinner" /></div>
            ) : items.length === 0 ? (
                <p className="text-center text-[#A0AAB2] py-12" data-testid="paypal-loads-empty">No PayPal loads yet.</p>
            ) : (
                <div className="space-y-6">
                    {grouped.map(([day, rows]) => {
                        const dayTotal = rows.reduce((s, r) => s + Number(r.amount_usd || 0), 0);
                        return (
                            <div key={day} data-testid={`paypal-loads-day-${day}`}>
                                <div className="flex items-center justify-between mb-2 pb-1 border-b border-white/10">
                                    <span className="text-[13px] font-black text-[#FFD700]">{day}</span>
                                    <span className="text-[12px] text-[#A0AAB2]">
                                        {rows.length} load{rows.length === 1 ? '' : 's'} · <span className="text-[#39FF14] font-bold font-mono">{money(dayTotal)}</span>
                                    </span>
                                </div>
                                <div className="space-y-1.5">
                                    {rows.map((r, i) => (
                                        <div key={i} data-testid="paypal-load-row"
                                             className="px-3 py-2.5 rounded-lg"
                                             style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                                            {/* who + when — full name on its own line for a quick glance */}
                                            <div className="flex items-baseline justify-between gap-2">
                                                <div className="text-white font-bold text-[14px] leading-snug truncate min-w-0" data-testid="load-name">{r.name}</div>
                                                <div className="text-[11px] text-[#A0AAB2] shrink-0">
                                                    {r.date ? asUTC(r.date).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: CT }) : ''}
                                                </div>
                                            </div>
                                            <div className="text-[11px] text-[#A0AAB2] truncate">{r.email}</div>
                                            {(() => {
                                                const FUND = { apple_pay: 'Apple Pay', google_pay: 'Google Pay', paypal: 'PayPal', card: 'Card', venmo: 'Venmo' };
                                                const label = FUND[r.funding] || (r.funding ? r.funding : null);
                                                if (!r.payer_email && !label) return null; // older loads have no payer recorded yet
                                                return (
                                                    <div className="text-[10px] text-[#7fd6ff] truncate" data-testid="load-payer">
                                                        {label && <span className="font-bold">{label}</span>}
                                                        {label && r.payer_email && ' · '}
                                                        {r.payer_email || ''}
                                                        {r.card_last4 && <span className="text-[#A0AAB2]"> ••{r.card_last4}</span>}
                                                    </div>
                                                );
                                            })()}
                                            {/* money + toggle */}
                                            <div className="flex items-center justify-between gap-3 mt-2">
                                                <div className="min-w-0">
                                                    <span className="text-[#39FF14] font-black font-mono text-[15px]">{money(r.amount_usd)}</span>
                                                    <span className="text-[11px] text-[#A0AAB2] ml-2">
                                                        +{r.credits} credits
                                                        {r.bonus > 0 && (
                                                            <span className="text-[#7bf0b0] font-bold"> +{r.bonus} <Gift className="inline w-3 h-3" /></span>
                                                        )}
                                                    </span>
                                                </div>
                                                <button onClick={() => toggleLoads(r)} disabled={busyUser === r.user_id}
                                                        data-testid={`load-toggle-${r.user_id}`}
                                                        title={r.loads_blocked ? 'Loads are OFF — click to allow this player to load again' : 'Loads are ON — click to block this player from loading'}
                                                        className="px-2.5 py-1.5 rounded-lg font-bold text-[11px] flex items-center gap-1.5 transition-colors shrink-0"
                                                        style={r.loads_blocked
                                                            ? { background: 'rgba(255,84,112,0.15)', border: '1px solid rgba(255,84,112,0.5)', color: '#ff5470' }
                                                            : { background: 'rgba(57,255,20,0.10)', border: '1px solid rgba(57,255,20,0.3)', color: '#39FF14' }}>
                                                    {r.loads_blocked ? <><Ban className="w-3.5 h-3.5" /> Loads OFF</> : <><CreditCard className="w-3.5 h-3.5" /> Loads ON</>}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
        </div>
    );
};

export default PayPalLoadsAdminTab;
